import { LogLevel, Occupancy } from "@variocube/app-api";
import {
    classNames,
    layout,
    PushButton,
    TabBar,
    TabItem,
    useAsync,
    usePlatform,
    useStorageItemValue,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem
} from "@variocube/cube-ui";
import * as React from "react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { stylesheet } from "typestyle";
import { PickupView } from "./pickup";
import {
    Bookmark,
    Delivery,
    DeliveryState,
    getOccupancyDeliveries,
    isDeliveryToBePickedUp,
    OccupancyType,
    PickupEvent,
    Recipient,
    TenantUser
} from "./types";
import { BoxTable } from "./BoxTable";
import { StorageFlow } from "./StorageFlow";
import { DeliveryTable } from "./DeliveryTable";
import { findByStorageKey, useKeyInput } from "./keys";
import { DeliveryView } from "./DeliveryView";
import { findDeliveryOccupancies, useStoreUserAction } from "./utils";
import { logger } from "./logger";
import { BookmarksList } from "./BookmarksList";
import { CreateDeliveryView } from "./CreateDeliveryView";
import { useLocalization } from "../i18n";
import { DeliveryFilter } from "./DeliveryFilter";

interface AdminProps {
    user: TenantUser;
    onDone: () => void;
}

const log = logger.sub("Admin");

export function Admin({ user, onDone }: AdminProps) {
    const platform = usePlatform();

    const [activeTab, setActiveTab] = useState(DeliveryHomeTab.Delivery);
    const [storage, setStorage] = useState<Delivery>();
    const [pickUp, setPickUp] = useState<PickupEvent>();
    const [delivery, setDelivery] = useState<Delivery>();
    const [addLocker, setAddLocker] = useState<Delivery>();

    const handleStorageDone = useCallback(() => setStorage(undefined), []);
    const handlePickupDone = useCallback(() => setPickUp(undefined), []);
    const handleAddLockerDone = useCallback(() => setAddLocker(undefined), []);
    const handleDeliveryClose = useCallback(() => setDelivery(undefined), []);

    const handleAddLocker = useCallback(() => {
        log.info("Adding locker to delivery", delivery?.delivery.deliveryId);
        setAddLocker(delivery);
    }, [delivery]);

    async function onPickupAll(deliveries: Delivery[]) {
        const allOccupancies: Occupancy[] = await platform.getOccupancies();
        let pickupOccupancies: Occupancy[] = [];
        for (let delivery of deliveries) {
            let occupancies = findDeliveryOccupancies(allOccupancies, delivery);
            pickupOccupancies.push(...occupancies);
        }
        setPickUp({ occupancies: pickupOccupancies , pickupRecipient: undefined});
        platform.boxes
            .filter((b) => b.types.includes("PrivateBox"))
            .forEach((b) => platform.openBoxLock(b.number));
    }

    const storeUserAction = useStoreUserAction();
    async function checkDelivery(delivery: Delivery) {
        log.info("Opening boxes for delivery", delivery);
        if (delivery.delivery && delivery.delivery.boxNumber) {
            await storeUserAction(delivery, user, "mark-checked");
            await platform.openBoxLock(delivery.delivery.boxNumber);
            await platform.log(LogLevel.Audit, "Check Box Open", { user });
        }
    }

    async function onCheckAll(deliveries: Delivery[]) {
        for (let delivery of deliveries) {
            await checkDelivery(delivery);
        }
    }

    const handlePickUpConfirm = useCallback(async (pickupEvent: PickupEvent, partialPickup: boolean) => {
        setPickUp(undefined);
        for (const occupancy of pickupEvent.occupancies) {
            try {
                if (partialPickup) {
                    await platform.log(LogLevel.Audit, "Staff partial Pickup", occupancy);
                }
                else {
                    log.info("Ending occupancy", occupancy);
                    const deliveries = getOccupancyDeliveries(occupancy) || [];
                    deliveries.forEach(delivery => {
                        if (!delivery.returnDelivery) {
                            delivery.delivery.state = DeliveryState.Cancelled;
                        } else {
                            delivery.returnDelivery.state = DeliveryState.PickedUp;
                        }

                        delivery.delivery.pickedUpBy = user.username;
                    });
                    log.info("Deliveries content", deliveries);
                    await platform.endOccupancy(occupancy.uuid, {
                        content: { deliveries }
                    });
                    await platform.log(LogLevel.Audit, "Staff Pickup", { occupancy, user });
                }
            } catch (error) {
                log.error("Error ending occupancy", error);
            }
        }
    }, [platform, user]);

    if (storage) {
        return (
            <StorageFlow
                delivery={storage}
                onDone={handleStorageDone}
                user={user}
            />
        );
    }
    else if (pickUp) {
        return (
            <PickupView
                pickupEvent={pickUp}
                onConfirm={handlePickUpConfirm}
                onCancel={handlePickupDone}
                canCancel
            />
        );
    }
    else if (addLocker) {
        return (
            <StorageFlow
                delivery={addLocker}
                onDone={handleAddLockerDone}
            />
        );
    }
    else if (delivery) {
        return (
            <DeliveryView
                delivery={delivery}
                onClose={handleDeliveryClose}
                onStore={setStorage}
                onPickUp={setPickUp}
                onAddLocker={handleAddLocker}
            />
        );
    }
    else {
        return (
            <DeliveryHome
                active={activeTab}
                user={user}
                onTabActivated={setActiveTab}
                onOpen={setDelivery}
                onPickupAll={onPickupAll}
                onCheckAll={onCheckAll}
                onCheck={checkDelivery}
                onDone={onDone}
            />
        );
    }
}

enum DeliveryHomeTab {
    Delivery,
    Boxes,
    Bookmarks,
    CreateDelivery
}

interface DeliveryHomeProps {
    active: DeliveryHomeTab;
    user: TenantUser;
    onTabActivated: (tab: DeliveryHomeTab) => void;
    onOpen: (delivery: Delivery) => void;
    onPickupAll: (deliveries: Delivery[]) => void;
    onCheckAll: (deliveries: Delivery[]) => void;
    onCheck: (delivery: Delivery) => void;
    onDone: () => void;
}


function DeliveryHome({ active, user, onTabActivated, onDone, onOpen, onPickupAll, onCheckAll, onCheck}: DeliveryHomeProps) {
    const platform = usePlatform();
    const { t } = useLocalization();
    const deliveries = useStorageItemValue<Delivery[]>("deliveries") || [];
    const recipients = useStorageItemValue<Recipient[]>("recipients") || [];
    const pickedUpDeliveries = useStorageItemValue<Delivery[]>("pickedUpDeliveries") || [];
    const bookmarks = useStorageItemValue<Bookmark[]>("bookmarks") || [];
    const { result: occupancies = [] } = useAsync(() => platform.getOccupancies(), [platform]);

	const internalBookmarks = useMemo(() =>
			(bookmarks ?? [])
				.filter(b => (!b.cubeId || b.cubeId === platform.hostname)
					&& !b.enableAnonymousDelivery
					&& !b.requireConfirmation
					&& !b.requireInternalRecipient
					&& !b.requireRecipientAddress
					&& !b.requireDispatchDetails
				)
		, [bookmarks, platform]);

    const handleKey = useCallback((key: string) => {
        if (deliveries) {
            const delivery = findByStorageKey(deliveries, key);
            if (delivery) {
                onOpen(delivery);
            }
        }
    }, [deliveries, onOpen]);
    useKeyInput(handleKey);

    function handleOccupancyCreated(occupancy: Occupancy) {
        onTabActivated(DeliveryHomeTab.Delivery);
    }

    const toBePickedUp = useMemo<Delivery[]>(() => deliveries.filter(isDeliveryToBePickedUp), [deliveries]);

    const allDeliveries = (deliveries.concat(pickedUpDeliveries));
    const [deliveriesFilter, setDeliveriesFilter] = useState<DeliveryFilter>();
    const filteredDeliveries = useMemo<Delivery[]>(() => (deliveries.concat(pickedUpDeliveries)).filter((delivery) => applyFilter(delivery, deliveriesFilter)), [deliveries, pickedUpDeliveries, deliveriesFilter]);

    const [showCheckAll, setShowCheckAll] = useState<boolean>();
    const [showPickupAll, setShowPickupAll] = useState<boolean>();

    const allAvailableTags = useMemo<string[]>(() => {
        let allTags: string[] = [];
        allDeliveries.forEach(d=>d.tags?.forEach(t=>{
            if (t && allTags.indexOf(t)<0)
                allTags.push(t);
        }))
        return allTags.sort();
    }, [allDeliveries]);

    useEffect(() => {
        const allPickedUp = filteredDeliveries.length > 0 && filteredDeliveries.length == filteredDeliveries.filter(d => d.delivery.state === DeliveryState.PickedUp).length;
        setShowCheckAll(allPickedUp);

        let oneMissing = false;
        toBePickedUp.forEach(d => {
            if (filteredDeliveries.filter(d1 => d1.delivery.deliveryId === d.delivery.deliveryId).length == 0)
                oneMissing = true;
        })
        setShowPickupAll(toBePickedUp.length > 0 && !oneMissing);


    }, [filteredDeliveries]);

    function applyFilter(delivery: Delivery, deliveriesFilter: DeliveryFilter | undefined): unknown {
        if (deliveriesFilter) {
            return (deliveriesFilter.states == undefined || deliveriesFilter.states?.length == 0 || hasState(delivery, deliveriesFilter.states))
                && (deliveriesFilter.tags == undefined || deliveriesFilter.tags?.length == 0 || hasTag(delivery, deliveriesFilter.tags))
                && (deliveriesFilter.occupancyTypes == undefined || deliveriesFilter.occupancyTypes?.length == 0 || hasOccupancyType(delivery, deliveriesFilter.occupancyTypes))
        }
        return true;
    }

    function hasState(delivery: Delivery, states: DeliveryState[]) {
        const deliveryState = delivery?.delivery?.state;
        return states.indexOf(deliveryState) >= 0;
    }

    function hasTag(delivery: Delivery, tags: string[]) {
        const deliveryTags = delivery?.tags;
        if (!deliveryTags)
            return false;
        return deliveryTags.filter(value => tags.includes(value)).length > 0;
    }

    function hasOccupancyType(delivery: Delivery, types: OccupancyType[]) {
        const deliveryType = delivery?.delivery.deliveryType;
        if (!deliveryType)
            return false;
        return types.indexOf(deliveryType as any) >= 0;
    }

    return (
        <View>
            <div className={classNames(layout.fixed, layout.horizontal, layout.spaceBetween)}>
                <TabBar>
                    <TabItem active={active == DeliveryHomeTab.Delivery}
                        onClick={() => onTabActivated(DeliveryHomeTab.Delivery)}>
                        {t("Deliveries")}
                    </TabItem>

                    {internalBookmarks.length > 0 &&
                        <TabItem active={active == DeliveryHomeTab.Bookmarks}
                            onClick={() => onTabActivated(DeliveryHomeTab.Bookmarks)}>
                            {t("Bookmarks")}
                        </TabItem>
                    }

                    <TabItem active={active == DeliveryHomeTab.Boxes}
                        onClick={() => onTabActivated(DeliveryHomeTab.Boxes)}>
                        {t("Boxes")}
                    </TabItem>

                </TabBar>
                <div className={styles.logoutButton} onClick={onDone}>
                    <div>{t("Logout")}</div>
                </div>
            </div>

            {active == DeliveryHomeTab.CreateDelivery && (
                <CreateDeliveryView
                    recipients={recipients}
                    onDone={handleOccupancyCreated}
                    user={user}
                />
            )}

            {(active != DeliveryHomeTab.Bookmarks && active != DeliveryHomeTab.CreateDelivery) &&
                <ViewContent className={styles.tabContent}>
                    {active == DeliveryHomeTab.Delivery && (
                        <Fragment>
                            <div className={styles.filterPanelContainer}>
                                <DeliveryFilter tags={allAvailableTags} filter={{...deliveriesFilter}} onApplyFilter={setDeliveriesFilter} />
                            </div>
                            <DeliveryTable
                                deliveries={filteredDeliveries}
                                occupancies={occupancies}
                                onOpen={onOpen}
                                onCheck={onCheck}
                            />
                        </Fragment>
                    )}
                    {active == DeliveryHomeTab.Boxes && (
                        <BoxTable />
                    )}
                </ViewContent>
            }
            {active == DeliveryHomeTab.Bookmarks &&
                <ViewContent>
                    <BookmarksList bookmarks={internalBookmarks} occupancies={occupancies} onOpen={onOpen} />
                </ViewContent>
            }

            {active != DeliveryHomeTab.CreateDelivery &&
                <ViewFooter className={layout.end}>
                    <ViewFooterItem className={layout.paddingMedium}>
                        <PushButton
                            onClick={() => onTabActivated(DeliveryHomeTab.CreateDelivery)} className={styles.action}
                        >
                            {t("CreateDelivery")}
                        </PushButton>


                        {showPickupAll && toBePickedUp?.length > 0 &&
                            <PushButton primary onClick={() => onPickupAll(toBePickedUp)} className={styles.action}>
                                {t("PickupAll")}
                            </PushButton>}

                        {showCheckAll &&
                            <PushButton primary onClick={() => onCheckAll(filteredDeliveries)} className={styles.action} >
                                {t("CheckAll")}
                            </PushButton>}

                    </ViewFooterItem>
                </ViewFooter>
            }
        </View>
    );
}

const styles = stylesheet({
    tabButton: {
        fontSize: '1rem'
    },
    tabContent: {
        padding: "0.75em",
        backgroundColor: "#fff"
    },
    logoutButton: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0.25rem 0.75rem",
        background: "var(--color-primary)",
        color: "#fff",
        textTransform: "uppercase",
        fontWeight: 500,
        fontSize: "1.25em",
    },
    action: {
        marginLeft: "1em",
        cursor: "pointer"
    },
    filterPanelContainer: {
        width: "100%",
        position: "relative",
        zIndex: 1,
        borderBottom: "1px solid black",
        paddingBottom: "20px"
    }
});
